// =============================================================================
// NGUỒN DUY NHẤT tính điểm tổng của một hồ sơ QCV.
//
// BỐI CẢNH (bug đã gặp): điểm tổng trước đây được tính LẠI ở từng nơi nên lệch
// nhau — màn "Chấm điểm hồ sơ" (HoSoNote) có cộng A.8 Công trình tiêu biểu
// nhưng KHÔNG cộng điểm thưởng đào tạo; còn "Xuất file chấm"
// (qcv.exportExcel) thì ngược lại: có điểm thưởng đào tạo nhưng BỎ QUÊN A.8.
// Giám khảo chấm A.8 trên màn hình xong, xuất file ra thì tổng bị hụt.
//
// Vì vậy: mọi nơi cần điểm tổng / điểm từng mục A-B-C-D đều phải gọi
// qcvScoreBreakdown(). ĐỪNG cộng tay form.scoreA + form.scoreB + ... nữa.
//
// LƯU Ý về nguồn dữ liệu:
//   - form.scoreA KHÔNG được backend lưu (model BCDScore chỉ có ScoreB..ScoreG),
//     nó là giá trị do reducer tự tính = tổng điểm các thành tích đã chấm. Ở đây
//     ta tính lại từ chính các mảng thành tích để không phụ thuộc reducer.
//   - Điểm A.8 nằm trong cột `typical_work` dạng JSON {tw1, tw2, tw3, tw4}.
// =============================================================================

import { qcvEnums } from "../constants/qcv.enums";

// A.8 Công trình tiêu biểu — trần từng tiêu chí, tổng tối đa 10 điểm.
export const QCV_TYPICAL_WORK_ROWS = [
  { key: "tw1", code: "8.1", label: "Tính mới, sáng tạo, đóng góp khoa học", max: 3 },
  { key: "tw2", code: "8.2", label: "Giá trị khoa học & uy tín học thuật", max: 3 },
  { key: "tw3", code: "8.3", label: "Vai trò & mức độ đóng góp", max: 2 },
  { key: "tw4", code: "8.4", label: "Giá trị thực tiễn & tác động xã hội", max: 2 },
];
export const QCV_TYPICAL_WORK_MAX = 10;

// Trần điểm các mục giám khảo tự chấm (theo bảng điểm QCV 2026).
export const QCV_SECTION_CAPS = { scoreB: 8, scoreC: 7, scoreD: 10 };

// Mỗi nội dung ưu tiên ứng viên tự khai (dân tộc thiểu số / người khuyết tật)
// được 5 điểm.
export const QCV_PRIORITY_POINT = 5;

const num = (v) => Number(v) || 0;
const round2 = (v) => Math.round(num(v) * 100) / 100;
const capped = (v, max) => Math.min(Math.max(num(v), 0), max);

export const safeParseJson = (raw, fallback) => {
  try {
    const parsed = JSON.parse(raw ?? "");
    return parsed ?? fallback;
  } catch (e) {
    return fallback;
  }
};

// Điểm A.8. Nhận chuỗi JSON đã lưu (form.typicalWork) HOẶC object {tw1..tw4}
// đang nhập trên màn chấm điểm.
export const qcvTypicalWorkScore = (typicalWork) => {
  const tw =
    typeof typicalWork === "string"
      ? safeParseJson(typicalWork, {})
      : typicalWork || {};
  const sum = QCV_TYPICAL_WORK_ROWS.reduce(
    (s, r) => s + capped(tw?.[r.key], r.max),
    0
  );
  return round2(Math.min(sum, QCV_TYPICAL_WORK_MAX));
};

// Điểm thưởng đào tạo nhân lực: thạc sĩ 2đ (>5 học viên: 3đ), cử nhân 2đ
// (>10 học viên: 3đ). Tính theo SỐ LƯỢNG học viên đã được duyệt, không phải
// điểm từng dòng — các dòng thạc sĩ/cử nhân không có điểm riêng.
export const qcvTrainingBonus = (formHrDevelopment) => {
  var numMaster = 0;
  var numBachelor = 0;
  (formHrDevelopment || []).forEach((hrForm) => {
    const hr = hrForm?.hrDevelopment;
    if (!hrForm?.accepted || hr?.type !== "daoTao") return;
    if (hr.academicLevel === "master") numMaster += 1;
    if (hr.academicLevel === "bachelor") numBachelor += 1;
  });
  const pointMaster = numMaster > 0 ? (numMaster > 5 ? 3 : 2) : 0;
  const pointBachelor = numBachelor > 0 ? (numBachelor > 10 ? 3 : 2) : 0;
  return { numMaster, numBachelor, pointMaster, pointBachelor };
};

// Điểm ưu tiên (D) TỰ ĐỘNG suy từ cột `priority` ứng viên đã khai.
//
// QUAN TRỌNG: màn chấm điểm chỉ ĐIỀN SẴN con số này vào ô nhập, nó chỉ thực sự
// vào cột score_d khi giám khảo bấm lưu/duyệt. Hồ sơ chưa ai bấm lưu thì
// score_d = 0 -> trước đây file xuất đọc thẳng score_d nên mục D ra 0 trong khi
// màn hình vẫn hiện 10. Dùng hàm này làm giá trị mặc định ở CẢ hai nơi.
export const qcvAutoPriorityScore = (priority) => {
  const p =
    typeof priority === "string" ? safeParseJson(priority, {}) : priority || {};
  return (
    (p?.isEthnicMinority ? QCV_PRIORITY_POINT : 0) +
    (p?.isDisabled ? QCV_PRIORITY_POINT : 0)
  );
};

// Tổng điểm các thành tích đã chấm (A.1-A.7 + đào tạo tiến sĩ).
export const qcvAchievementScore = (form) => {
  var total = 0;
  qcvEnums.THANH_TICH_LIST.forEach((key) => {
    (form?.[key] || []).forEach((e) => {
      total += num(e?.score);
    });
  });
  return round2(total);
};

// Điểm A/B/C/D + tổng của một hồ sơ.
// `draft` (tuỳ chọn) là các giá trị giám khảo ĐANG nhập trên HoSoNote
// ({tw1..tw4, scoreB, scoreC, scoreD}); có thì ưu tiên hơn giá trị đã lưu.
export const qcvScoreBreakdown = (form, draft) => {
  const { pointMaster, pointBachelor } = qcvTrainingBonus(form?.formHrDevelopment);
  const trainingBonus = pointMaster + pointBachelor;

  const hasDraftTw =
    !!draft && QCV_TYPICAL_WORK_ROWS.some((r) => draft[r.key] != null);
  const typicalWork = qcvTypicalWorkScore(hasDraftTw ? draft : form?.typicalWork);

  const achievement = qcvAchievementScore(form);
  const scoreA = round2(achievement + trainingBonus + typicalWork);

  const pick = (key) =>
    draft && draft[key] != null && draft[key] !== "" && !Number.isNaN(draft[key])
      ? draft[key]
      : form?.[key];
  const scoreB = capped(pick("scoreB"), QCV_SECTION_CAPS.scoreB);
  const scoreC = capped(pick("scoreC"), QCV_SECTION_CAPS.scoreC);
  // D: chưa chấm/chưa lưu thì lấy điểm ưu tiên tự động, y như màn chấm điểm.
  const autoPriority = qcvAutoPriorityScore(form?.priority);
  const rawD = pick("scoreD");
  const scoreD = capped(
    num(rawD) > 0 ? rawD : autoPriority,
    QCV_SECTION_CAPS.scoreD
  );

  return {
    achievement,
    trainingBonus,
    pointMaster,
    pointBachelor,
    typicalWork,
    autoPriority,
    scoreA,
    scoreB,
    scoreC,
    scoreD,
    total: round2(scoreA + scoreB + scoreC + scoreD),
  };
};

// Tiện dụng cho các chỗ chỉ cần sắp xếp theo tổng điểm.
export const qcvTotalScore = (form) => qcvScoreBreakdown(form).total;
